import * as React from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService, utilService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import { formatDatetimeJST } from '../../../../utils/dateFormat'
import { Button, Panel } from '../../../atoms'
import SidebarLayout from '../../_layouts/sidebarLayout'
import SvgIcon from 'components/atoms/SvgIcon'
import { truncateText } from './diffUtils'
import { TEXT_INITIAL_LINE_COUNT } from './constants'

interface IUnagreedGuide {
  id: number
  fullname: string
  email: string
  last_sign_in_at: string | null
}

interface IProps {
  terms_version: IJsonResponse
  agreed_guides: IJsonResponse
  viewed_unagreed_guides: IJsonResponse
  unagreed_guides: IUnagreedGuide[]
  user: IJsonResponse
}

const STATUS_LABELS: { [key: string]: string } = {
  draft: '下書き',
  scheduled: '公開予定',
  in_effect: '施行中',
  deleted: '削除済み',
}

const INITIAL_DISPLAY_COUNT = 15

const AgentTermsVersionShow: React.FC<IProps> = props => {
  const { data: userData } = utilService.getDataFromJson(props.user)
  const { data: termsVersion } = agentService.getDataFromJson(props.terms_version)
  const { data: agreedGuides } = agentService.getDataFromJson(props.agreed_guides)
  const { data: viewedUnagreedGuides } = agentService.getDataFromJson(props.viewed_unagreed_guides)
  const unagreedGuides = props.unagreed_guides || []

  const [showAllAgreed, setShowAllAgreed] = React.useState(false)
  const [showAllViewedUnagreed, setShowAllViewedUnagreed] = React.useState(false)
  const [showAllUnagreed, setShowAllUnagreed] = React.useState(false)
  const [showAllBody, setShowAllBody] = React.useState(false)
  const [showAllDiff, setShowAllDiff] = React.useState(false)
  const [isBodyCopied, setIsBodyCopied] = React.useState(false)

  // 公開済み（施行中）の規約のみ本文コピーを許可する
  const isPublished = termsVersion.display_status === 'in_effect'

  const bodyText = termsVersion.body || '-'
  const truncatedBody = truncateText(bodyText, TEXT_INITIAL_LINE_COUNT)
  const visibleBodyText = showAllBody ? bodyText : truncatedBody.text

  // diff_against_previous: null=前回 active 規約なし / ''=変更なし / 文字列=変更行のみ
  const rawDiff = termsVersion.diff_against_previous
  const hasPrevDiff = typeof rawDiff === 'string' && rawDiff.length > 0
  const diffPlaceholder =
    rawDiff === null || rawDiff === undefined
      ? '前回 active 規約がないため差分なし'
      : '前回 active 規約から変更なし'
  const diffText = hasPrevDiff ? rawDiff : diffPlaceholder
  const truncatedDiff = truncateText(diffText, TEXT_INITIAL_LINE_COUNT)
  const visibleDiffText = showAllDiff ? diffText : truncatedDiff.text

  const copiedTimerRef = React.useRef<number | undefined>(undefined)

  // アンマウント時にコピー表示用タイマーを破棄する
  React.useEffect(() => {
    return () => window.clearTimeout(copiedTimerRef.current)
  }, [])

  // 本文全文をクリップボードへコピーする。非セキュアコンテキストや権限拒否時は失敗するため握る
  const copyBodyToClipboard = async () => {
    try {
      if (!window.navigator.clipboard) {
        throw new Error('clipboard API is unavailable')
      }
      await window.navigator.clipboard.writeText(termsVersion.body || '')
      setIsBodyCopied(true)
      window.clearTimeout(copiedTimerRef.current)
      copiedTimerRef.current = window.setTimeout(() => {
        setIsBodyCopied(false)
      }, 3000)
    } catch (e) {
      // コピー失敗時は成功表示を出さない
      window.alert('コピーに失敗しました。お手数ですが手動でコピーしてください。')
    }
  }

  const visibleAgreedGuides = showAllAgreed
    ? agreedGuides
    : agreedGuides.slice(0, INITIAL_DISPLAY_COUNT)
  const visibleViewedUnagreedGuides = showAllViewedUnagreed
    ? viewedUnagreedGuides
    : viewedUnagreedGuides.slice(0, INITIAL_DISPLAY_COUNT)
  const visibleUnagreedGuides = showAllUnagreed
    ? unagreedGuides
    : unagreedGuides.slice(0, INITIAL_DISPLAY_COUNT)

  return (
    <SidebarLayout user={userData}>
      <Wrapper>
        <Panel
          title={
            <S.PanelTitleRow>
              <span>ガイド利用規約 - {termsVersion.name}</span>
              <S.HeaderEditButton href={`/agent/terms_versions/${termsVersion.id}/edit`}>
                {I18n.t('generic.edit')}
              </S.HeaderEditButton>
            </S.PanelTitleRow>
          }
        >
          <S.DetailWrapper>
            <S.DetailRow>
              <S.DetailLabel>{I18n.t('generic.id')}</S.DetailLabel>
              <S.DetailValue>{termsVersion.id}</S.DetailValue>
            </S.DetailRow>

            <S.DetailRow>
              <S.DetailLabel>名前</S.DetailLabel>
              <S.DetailValue>{termsVersion.name}</S.DetailValue>
            </S.DetailRow>

            <S.DetailRow>
              <S.DetailLabel>メッセージ</S.DetailLabel>
              <S.DetailValue>
                <pre>{termsVersion.message || '-'}</pre>
              </S.DetailValue>
            </S.DetailRow>

            <S.DetailRow>
              <S.DetailLabel>状態</S.DetailLabel>
              <S.DetailValue>
                {STATUS_LABELS[termsVersion.display_status] || termsVersion.display_status}
              </S.DetailValue>
            </S.DetailRow>

            <S.DetailRow>
              <S.DetailLabel>施行日時</S.DetailLabel>
              <S.DetailValue>{formatDatetimeJST(termsVersion.effective_at)}</S.DetailValue>
            </S.DetailRow>

            <S.DetailRow>
              <S.DetailLabel>規約本文</S.DetailLabel>
              <S.DetailValue>
                {isPublished && (
                  <S.CopyWrapper>
                    <S.CopyButton type="button" onClick={copyBodyToClipboard}>
                      本文をコピー
                    </S.CopyButton>
                    {isBodyCopied && <S.CopiedBadge>コピーしました</S.CopiedBadge>}
                  </S.CopyWrapper>
                )}
                <pre>{visibleBodyText}</pre>
                {!showAllBody && truncatedBody.isTruncated && (
                  <S.ShowMoreWrapper>
                    <S.ShowMoreButton type="button" onClick={() => setShowAllBody(true)}>
                      もっと見る (残り {truncatedBody.totalLines - TEXT_INITIAL_LINE_COUNT} 行)
                    </S.ShowMoreButton>
                  </S.ShowMoreWrapper>
                )}
              </S.DetailValue>
            </S.DetailRow>

            <S.DetailRow>
              <S.DetailLabel>前回 active 規約との差分</S.DetailLabel>
              <S.DetailValue>
                <pre>{visibleDiffText}</pre>
                {!showAllDiff && truncatedDiff.isTruncated && (
                  <S.ShowMoreWrapper>
                    <S.ShowMoreButton type="button" onClick={() => setShowAllDiff(true)}>
                      もっと見る (残り {truncatedDiff.totalLines - TEXT_INITIAL_LINE_COUNT} 行)
                    </S.ShowMoreButton>
                  </S.ShowMoreWrapper>
                )}
              </S.DetailValue>
            </S.DetailRow>

            <S.DetailRow>
              <S.DetailLabel>同意済みガイド</S.DetailLabel>
              <S.DetailValue>
                {termsVersion.agreed_count} /{' '}
                {termsVersion.agreed_count + termsVersion.unagreed_count} (
                {termsVersion.agreement_rate}%)
              </S.DetailValue>
            </S.DetailRow>

            <S.SectionHeading>同意済みガイド一覧</S.SectionHeading>
            <S.CsvLink href={`/agent/terms_versions/${termsVersion.id}/agreed_guides_csv.csv`}>
              同意済みガイドCSVダウンロード
            </S.CsvLink>
            <S.Table>
              <thead>
                <tr>
                  <th>ガイドID</th>
                  <th>名前</th>
                  <th>メール</th>
                  <th>表示日時</th>
                  <th>同意日時</th>
                  <th>アプリバージョン</th>
                  <th>IPアドレス</th>
                </tr>
              </thead>
              <tbody>
                {agreedGuides.length === 0 ? (
                  <tr>
                    <td colSpan={7}>同意済みのガイドはまだいません</td>
                  </tr>
                ) : (
                  visibleAgreedGuides.map(a => (
                    <tr key={a.id}>
                      <td>{a.guide_id}</td>
                      <td>{a.guide_name || '-'}</td>
                      <td>{a.guide_email || '-'}</td>
                      <td>{formatDatetimeJST(a.viewed_at)}</td>
                      <td>{formatDatetimeJST(a.agreed_at)}</td>
                      <td>{a.app_version || '-'}</td>
                      <td>{a.ip_address || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </S.Table>
            {!showAllAgreed && agreedGuides.length > INITIAL_DISPLAY_COUNT && (
              <S.ShowMoreWrapper>
                <S.ShowMoreButton type="button" onClick={() => setShowAllAgreed(true)}>
                  もっと見る ({agreedGuides.length - INITIAL_DISPLAY_COUNT}件)
                </S.ShowMoreButton>
              </S.ShowMoreWrapper>
            )}

            <S.SectionHeading>未同意ガイド一覧（閲覧済み）</S.SectionHeading>
            <S.Table>
              <thead>
                <tr>
                  <th>ガイドID</th>
                  <th>名前</th>
                  <th>メール</th>
                  <th>表示日時</th>
                  <th>アプリバージョン</th>
                  <th>IPアドレス</th>
                </tr>
              </thead>
              <tbody>
                {viewedUnagreedGuides.length === 0 ? (
                  <tr>
                    <td colSpan={6}>閲覧済みで未同意のガイドはいません</td>
                  </tr>
                ) : (
                  visibleViewedUnagreedGuides.map(v => (
                    <tr key={v.id}>
                      <td>{v.guide_id}</td>
                      <td>{v.guide_name || '-'}</td>
                      <td>{v.guide_email || '-'}</td>
                      <td>{formatDatetimeJST(v.viewed_at)}</td>
                      <td>{v.app_version || '-'}</td>
                      <td>{v.ip_address || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </S.Table>
            {!showAllViewedUnagreed && viewedUnagreedGuides.length > INITIAL_DISPLAY_COUNT && (
              <S.ShowMoreWrapper>
                <S.ShowMoreButton type="button" onClick={() => setShowAllViewedUnagreed(true)}>
                  もっと見る ({viewedUnagreedGuides.length - INITIAL_DISPLAY_COUNT}件)
                </S.ShowMoreButton>
              </S.ShowMoreWrapper>
            )}

            <S.SectionHeading>未同意ガイド一覧（全体）</S.SectionHeading>
            <S.CsvLink href={`/agent/terms_versions/${termsVersion.id}/unagreed_guides_csv.csv`}>
              未同意ガイドCSVダウンロード
            </S.CsvLink>
            <S.Table>
              <thead>
                <tr>
                  <th>ガイドID</th>
                  <th>名前</th>
                  <th>メール</th>
                  <th>最終ログイン日時</th>
                </tr>
              </thead>
              <tbody>
                {unagreedGuides.length === 0 ? (
                  <tr>
                    <td colSpan={4}>未同意のガイドはいません</td>
                  </tr>
                ) : (
                  visibleUnagreedGuides.map(g => (
                    <tr key={g.id}>
                      <td>{g.id}</td>
                      <td>{g.fullname || '-'}</td>
                      <td>{g.email || '-'}</td>
                      <td>{g.last_sign_in_at ? formatDatetimeJST(g.last_sign_in_at) : '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </S.Table>
            {!showAllUnagreed && unagreedGuides.length > INITIAL_DISPLAY_COUNT && (
              <S.ShowMoreWrapper>
                <S.ShowMoreButton type="button" onClick={() => setShowAllUnagreed(true)}>
                  もっと見る ({unagreedGuides.length - INITIAL_DISPLAY_COUNT}件)
                </S.ShowMoreButton>
              </S.ShowMoreWrapper>
            )}

            <div className="Buttons">
              <Button>
                <a href="/agent/terms_versions">
                  <SvgIcon
                    name="keyboard_arrow_left"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  {I18n.t('generic.back_to_index')}
                </a>
              </Button>
              <Button primary={true}>
                <a href={`/agent/terms_versions/${termsVersion.id}/edit`}>
                  {I18n.t('generic.edit')}
                </a>
              </Button>
            </div>
          </S.DetailWrapper>
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}

S.DetailWrapper = styled.div`
  padding: 0 24px;
`

S.DetailRow = styled.div`
  display: flex;
  padding: 16px 0;
  border-bottom: solid 1px ${constants.COLORS.divider};

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
    gap: 8px;
  }
`

S.DetailLabel = styled.div`
  min-width: 240px;
  font-size: 14px;
  font-weight: bold;
  color: ${constants.COLORS.subText};

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    min-width: auto;
  }
`

S.DetailValue = styled.div`
  flex: 1;
  font-size: 14px;
  word-break: break-all;

  pre {
    white-space: pre-wrap;
    font-family: inherit;
    margin: 0;
  }
`

S.SectionHeading = styled.h3`
  margin-top: 32px;
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: bold;
`

S.CsvLink = styled.a`
  display: inline-block;
  margin-bottom: 12px;
  color: ${constants.COLORS.primary};
  text-decoration: underline;
  font-size: 13px;
`

S.PanelTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

S.HeaderEditButton = styled.a`
  display: inline-block;
  padding: 6px 18px;
  font-size: 14px;
  font-weight: normal;
  color: #fff;
  background: ${constants.COLORS.primary};
  border-radius: 4px;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    opacity: 0.85;
    color: #fff;
    text-decoration: none;
  }
`

S.ShowMoreWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;
`

S.ShowMoreButton = styled.button`
  padding: 8px 24px;
  font-size: 13px;
  color: ${constants.COLORS.primary};
  background: transparent;
  border: solid 1px ${constants.COLORS.primary};
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${constants.COLORS.primary};
    color: #fff;
  }
`

S.CopyWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

S.CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  font-size: 13px;
  color: ${constants.COLORS.primary};
  background: transparent;
  border: solid 1px ${constants.COLORS.primary};
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${constants.COLORS.primary};
    color: #fff;
  }
`

S.CopiedBadge = styled.span`
  font-size: 12px;
  color: #fff;
  background-color: ${constants.COLORS.successText};
  border-radius: 20px;
  padding: 4px 10px;
`

S.Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    border: solid 1px ${constants.COLORS.divider};
    padding: 6px 10px;
    text-align: left;
  }

  th {
    background: ${constants.COLORS.gray};
    font-weight: bold;
  }
`

const Wrapper = styled.div`
  width: 100%;
  padding: 22px 40px 80px 46px;

  .Panel {
    max-width: none;
  }

  .Buttons {
    position: relative;
    display: flex;
    justify-content: center;
    margin-top: 48px;
    margin-bottom: 24px;

    .Button[type='button'] {
      position: absolute;
      left: 0;
      top: 0;
      display: flex;
      align-items: center;

      .icon {
        margin-left: -10px;
      }
    }

    .Button a {
      color: inherit;
      text-decoration: none;
    }
  }

  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    padding: 16px 12px;
  }
`

export default AgentTermsVersionShow
